import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import {
  Users,
  MessageCircle,
  BookOpen,
  CheckCircle,
  AlertTriangle,
  Clock,
  Send,
  Loader2,
  Search,
  TrendingUp,
  UserCheck,
  XCircle,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Avatar, AvatarFallback } from '../components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ParentEngagementPage = () => {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [messageLoading, setMessageLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchOverview = async () => {
    try {
      const response = await fetch(`${API_URL}/api/parent/staff/engagement-overview`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setOverview(data);
      }
    } catch (error) {
      console.error('Overview error:', error);
      toast.error('Veriler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchThreads = async () => {
    try {
      const response = await fetch(`${API_URL}/api/parent/staff/messages`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setThreads(data);
      }
    } catch (error) {
      console.error('Threads error:', error);
    }
  };

  const fetchMessages = async (threadId) => {
    try {
      setMessageLoading(true);
      const response = await fetch(`${API_URL}/api/parent/messages/threads/${threadId}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages);
      }
    } catch (error) {
      console.error('Messages error:', error);
    } finally {
      setMessageLoading(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    fetchThreads();
  }, []);

  useEffect(() => {
    if (selectedThread) {
      fetchMessages(selectedThread.thread_id);
    }
  }, [selectedThread]);

  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedThread) return;

    try {
      const response = await fetch(`${API_URL}/api/parent/staff/messages/threads/${selectedThread.thread_id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newMessage }),
      });

      if (response.ok) {
        const msg = await response.json();
        setMessages(prev => [...prev, msg]);
        setNewMessage('');
        fetchThreads(); // Refresh thread list
      }
    } catch (error) {
      toast.error('Mesaj gönderilemedi');
    }
  };

  const filteredStudents = overview?.students?.filter(s => 
    s.student_name.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="parent-engagement-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-heading font-bold text-primary-900">Veli Katılımı</h1>
        <p className="text-muted-foreground">Veli aktivitelerini ve iletişimi takip edin</p>
      </div>

      {/* Summary Stats */}
      {overview?.summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-primary/10">
                  <Users className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{overview.summary.total_students}</div>
                  <p className="text-xs text-muted-foreground">Toplam Öğrenci</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-green-100">
                  <UserCheck className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{overview.summary.guardian_coverage}%</div>
                  <p className="text-xs text-muted-foreground">Veli Kaydı</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-blue-100">
                  <BookOpen className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{overview.summary.active_readers_7d}</div>
                  <p className="text-xs text-muted-foreground">Aktif Okuyucu (7 gün)</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-full bg-secondary/10">
                  <TrendingUp className="h-5 w-5 text-secondary" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{overview.summary.active_reading_rate}%</div>
                  <p className="text-xs text-muted-foreground">Okuma Oranı</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Student List */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-lg">Öğrenci Durumları</CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Öğrenci ara..."
                className="pl-9"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {filteredStudents.map(student => (
                <div
                  key={student.student_id}
                  className="flex items-center justify-between p-3 rounded-lg border hover:bg-slate-50"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback>{student.student_name[0]}</AvatarFallback>
                    </Avatar>
                    <div>
                      <p className="font-medium">{student.student_name}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {student.guardians?.length > 0 ? (
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="h-3 w-3" />
                            {student.guardians.length} veli
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-red-500">
                            <XCircle className="h-3 w-3" />
                            Veli kaydı yok
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <div className="flex items-center gap-1">
                        <BookOpen className={`h-4 w-4 ${
                          student.reading_days_last_week >= 5 ? 'text-green-600' :
                          student.reading_days_last_week >= 3 ? 'text-yellow-600' : 'text-red-500'
                        }`} />
                        <span className="text-sm font-medium">{student.reading_days_last_week}/7</span>
                      </div>
                      <p className="text-xs text-muted-foreground">bu hafta</p>
                    </div>
                    {student.has_active_guardian ? (
                      <Badge className="bg-green-100 text-green-700">Aktif</Badge>
                    ) : student.guardians?.length > 0 ? (
                      <Badge variant="secondary">Pasif</Badge>
                    ) : (
                      <Badge variant="outline" className="text-red-500 border-red-200">Eksik</Badge>
                    )}
                  </div>
                </div>
              ))}
              {filteredStudents.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  Öğrenci bulunamadı
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Messages */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center justify-between">
              <span>Veli Mesajları</span>
              {threads.filter(t => t.unread_count > 0).length > 0 && (
                <Badge>{threads.filter(t => t.unread_count > 0).length} yeni</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {threads.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">
                  Henüz mesaj yok
                </p>
              ) : (
                threads.map(thread => (
                  <button
                    key={thread.thread_id}
                    className={`w-full p-3 rounded-lg text-left transition-colors border ${
                      thread.unread_count > 0 ? 'bg-blue-50 border-blue-200' : 'hover:bg-slate-50'
                    }`}
                    onClick={() => setSelectedThread(thread)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-sm">{thread.guardian_name}</span>
                      {thread.unread_count > 0 && (
                        <Badge className="h-5 w-5 p-0 flex items-center justify-center">
                          {thread.unread_count}
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{thread.subject}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Re: {thread.student_name}
                    </p>
                  </button>
                ))
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Message Dialog */}
      <Dialog open={!!selectedThread} onOpenChange={() => setSelectedThread(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedThread?.subject}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {selectedThread?.guardian_name} • {selectedThread?.student_name}
            </p>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="h-64 overflow-y-auto space-y-3 border rounded-lg p-3">
              {messageLoading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin" />
                </div>
              ) : (
                messages.map(msg => (
                  <div
                    key={msg.message_id}
                    className={`flex ${msg.direction === 'personelden' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div
                      className={`max-w-[80%] p-3 rounded-lg ${
                        msg.direction === 'personelden'
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-slate-100'
                      }`}
                    >
                      <p className="text-sm">{msg.content}</p>
                      <p className={`text-xs mt-1 ${
                        msg.direction === 'personelden' ? 'text-primary-foreground/70' : 'text-muted-foreground'
                      }`}>
                        {msg.sender_name} • {new Date(msg.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>
                ))
              )}
            </div>
            
            <div className="flex gap-2">
              <Input
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Yanıtınızı yazın..."
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
              />
              <Button onClick={handleSendMessage} disabled={!newMessage.trim()}>
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ParentEngagementPage;
